import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ISSUE_TYPES = [
  'Discipline Concern',
  'Safety Concern',
  'Overtime Dispute',
  'Working Conditions',
  'Contract Violation',
  'Harassment',
  'Equipment Issue',
  'Other'
];

const CLASSIFICATIONS = [
  'Information Only',
  'Informal Review',
  'Formal Grievance',
  'Safety Report'
];

function ReportIssuePage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pre-populate description if coming from Q&A
  const initialQuestion = location.state?.question || '';
  
  const [formData, setFormData] = useState({
    issue_type: '',
    description: initialQuestion ? `Related to question: "${initialQuestion}"\n\n` : '',
    location: '',
    classification: '',
    morale_score: 3,
    is_anonymous: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.issue_type || !formData.description.trim()) {
      setError('Please select an issue type and provide a description');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/ask-stewie/analytics/report`,
        {
          issue_type: formData.issue_type,
          description: formData.description.trim(),
          location: formData.location || null,
          classification: formData.classification || null,
          morale_score: formData.morale_score,
          is_anonymous: formData.is_anonymous
        },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      
      setSuccess({
        message: res.data.message,
        reportId: res.data.report_id,
        anonymous: res.data.anonymous
      });
      
      // Reset form
      setFormData({
        issue_type: '',
        description: '',
        location: '',
        classification: '',
        morale_score: 3,
        is_anonymous: false
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      issue_type: '',
      description: '',
      location: '',
      classification: '',
      morale_score: 3,
      is_anonymous: false
    });
    setError('');
    setSuccess(null);
  };

  return (
    <div className="as-analytics-page" data-testid="report-issue-page">
      <header className="as-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <img src="/stewie_front.png" alt="Stewie" data-testid="stewie-logo" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%', border: '2px solid #334155' }} />
          <div className="as-badge">Member Support</div>
        </div>
        <h1>Report an Issue</h1>
        <p>Submit workplace concerns anonymously or with your identity for tracking.</p>
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
      </header>

      <div className="as-card">
        <div className="card-header">
          <h2>Issue Report Form</h2>
          <p>All reports are reviewed and tracked for resolution</p>
        </div>
        <div className="card-body">
          {success && (
            <div className="success-panel" data-testid="success-panel">
              <div className="success-icon">✓</div>
              <div className="success-content">
                <div className="success-title">{success.message}</div>
                <div className="success-details">
                  Report ID: #{success.reportId}
                  {success.anonymous && <span className="anonymous-badge">Submitted Anonymously</span>}
                </div>
              </div>
              <button className="btn-primary" onClick={handleClear}>Submit Another</button>
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit}>
              <div className="field-group">
                <label>Issue Type *</label>
                <select
                  name="issue_type"
                  value={formData.issue_type}
                  onChange={handleInputChange}
                  className="select-input"
                  data-testid="issue-type-select"
                >
                  <option value="">Select an issue type...</option>
                  {ISSUE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="field-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the issue in detail. Include dates, times, and people involved if relevant."
                  data-testid="description-input"
                />
              </div>

              <div className="field-row">
                <div className="field-group half">
                  <label>Location (Optional)</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., Building A, Floor 2"
                    className="text-input"
                    data-testid="location-input"
                  />
                </div>

                <div className="field-group half">
                  <label>Classification (Optional)</label>
                  <select
                    name="classification"
                    value={formData.classification}
                    onChange={handleInputChange}
                    className="select-input"
                    data-testid="classification-select"
                  >
                    <option value="">Select classification...</option>
                    {CLASSIFICATIONS.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-group">
                <label>Workplace Morale Score: {formData.morale_score}/5</label>
                <input
                  type="range"
                  name="morale_score"
                  min="1"
                  max="5"
                  value={formData.morale_score}
                  onChange={handleInputChange}
                  className="range-input"
                  data-testid="morale-slider"
                />
                <div className="range-labels">
                  <span>Very Low</span>
                  <span>Neutral</span>
                  <span>Very High</span>
                </div>
              </div>

              <div className="field-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_anonymous"
                    checked={formData.is_anonymous}
                    onChange={handleInputChange}
                    data-testid="anonymous-checkbox"
                  />
                  <span className="checkmark"></span>
                  Submit Anonymously
                </label>
                <p className="field-hint">
                  Anonymous reports cannot be tracked for follow-up, but your identity will be protected.
                </p>
              </div>

              {error && <div className="error-message" data-testid="error">{error}</div>}

              <div className="button-row">
                <button 
                  type="submit"
                  className="btn-primary" 
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Report'}
                </button>
                <button type="button" className="btn-secondary" onClick={handleClear}>Clear Form</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportIssuePage;
